import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { getTenant } from '../api/tenants.js';
import { TenantWorkspaceUrl } from '../components/TenantWorkspaceUrl.js';
import { t } from '../i18n/es.js';

export function TenantDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => getTenant(id),
    enabled: Boolean(id),
  });

  if (isLoading) return <p>{t.app.loading}</p>;
  if (error) return <p className="text-red-700">{(error as Error).message}</p>;
  if (!data) return <p>{t.tenant.empty}</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.tenant.detailTitle}</h2>
        <Link
          to={`/tenants/${data.id}/edit`}
          className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
        >
          {t.tenant.editBtn}
        </Link>
      </div>
      <div className="mb-4">
        <TenantWorkspaceUrl slug={data.slug} />
      </div>
      <dl className="grid grid-cols-2 gap-3 rounded border border-slate-200 bg-white p-4 text-sm">
        <dt className="text-slate-500">{t.tenant.slug}</dt>
        <dd>{data.slug}</dd>
        <dt className="text-slate-500">{t.tenant.name}</dt>
        <dd>{data.name}</dd>
        <dt className="text-slate-500">{t.tenant.tier}</dt>
        <dd>{data.tier}</dd>
        <dt className="text-slate-500">{t.tenant.locale}</dt>
        <dd>{data.defaultLocale ?? '—'}</dd>
        <dt className="text-slate-500">{t.tenant.featureFlags}</dt>
        <dd>
          <pre className="overflow-auto text-xs">
            {JSON.stringify(data.featureFlags ?? {}, null, 2)}
          </pre>
        </dd>
      </dl>
      <div className="mt-4 flex gap-2">
        <Link to={`/tenants/${data.id}/dashboard`} className="rounded border px-3 py-1 text-sm">
          Dashboard
        </Link>
        <Link to={`/tenants/${data.id}/channels`} className="rounded border px-3 py-1 text-sm">
          {t.nav.channels}
        </Link>
        <Link to={`/tenants/${data.id}/contacts`} className="rounded border px-3 py-1 text-sm">
          Contactos
        </Link>
        <Link to={`/tenants/${data.id}/connectors`} className="rounded border px-3 py-1 text-sm">
          {t.nav.connectors}
        </Link>
        <Link to={`/tenants/${data.id}/cc-connectors`} className="rounded border px-3 py-1 text-sm">
          Contact Center
        </Link>
        <Link to={`/tenants/${data.id}/templates`} className="rounded border px-3 py-1 text-sm">
          {t.nav.templates}
        </Link>
        <Link to={`/tenants/${data.id}/campaigns`} className="rounded border px-3 py-1 text-sm">
          {t.nav.campaigns}
        </Link>
        <Link to={`/tenants/${data.id}/surveys`} className="rounded border px-3 py-1 text-sm">
          {t.nav.surveys}
        </Link>
        <Link to={`/tenants/${data.id}/widgets`} className="rounded border px-3 py-1 text-sm">
          {t.nav.widgets}
        </Link>
        <Link to={`/tenants/${data.id}/users`} className="rounded border px-3 py-1 text-sm">
          {t.nav.users}
        </Link>
        <Link to={`/tenants/${data.id}/flows`} className="rounded border px-3 py-1 text-sm">
          {t.nav.flows}
        </Link>
        <Link to={`/tenants/${data.id}/widget`} className="rounded border px-3 py-1 text-sm">
          {t.nav.widget}
        </Link>
      </div>
    </div>
  );
}
